import { useCallback, useEffect, useState, type FormEvent } from "react";
import { createUser, deleteUser, listUsers, type InspectorUser, type UserStatus } from "../api";
import { formatTimestamp } from "../format";

// Who may sign in here, and the two things one can do about it: invite someone,
// remove someone. The pool has no sign-up path, so this list IS the access
// list. Removing is confirmed in place — access ends at once — and the API
// refuses to remove you, or the last account, so the board cannot lock
// everyone out.

const STATUS_LABEL: Record<UserStatus, string> = {
  FORCE_CHANGE_PASSWORD: "Invited — not signed in yet",
  CONFIRMED: "Active",
  RESET_REQUIRED: "Password reset required",
  UNCONFIRMED: "Unconfirmed",
  ARCHIVED: "Archived",
  COMPROMISED: "Compromised",
  UNKNOWN: "Unknown",
};

const message = (err: unknown): string => (err instanceof Error ? err.message : String(err));

export function UserAccess({ token, viewer }: { token: string; viewer: string }): JSX.Element {
  const [users, setUsers] = useState<InspectorUser[] | undefined>();
  const [loadError, setLoadError] = useState<string | undefined>();
  const [email, setEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  const [notice, setNotice] = useState<{ tone: "ok" | "bad"; text: string } | undefined>();
  const [confirming, setConfirming] = useState<string | undefined>();
  const [removing, setRemoving] = useState<string | undefined>();

  const reload = useCallback(() => {
    listUsers(token)
      .then((list) => {
        setUsers(list);
        setLoadError(undefined);
      })
      .catch((err: unknown) => setLoadError(message(err)));
  }, [token]);

  useEffect(() => {
    let cancelled = false;
    listUsers(token)
      .then((list) => {
        if (!cancelled) setUsers(list);
      })
      .catch((err: unknown) => {
        if (!cancelled) setLoadError(message(err));
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const invite = (event: FormEvent): void => {
    event.preventDefault();
    const address = email.trim();
    if (address === "") return;
    setInviting(true);
    setNotice(undefined);
    createUser(token, address)
      .then((user) => {
        setEmail("");
        setNotice({
          tone: "ok",
          text: `Invitation sent to ${user.email}. Cognito has emailed a temporary password, valid for seven days; the sign-in screen walks them through choosing their own.`,
        });
        reload();
      })
      .catch((err: unknown) => setNotice({ tone: "bad", text: message(err) }))
      .finally(() => setInviting(false));
  };

  const remove = (user: InspectorUser): void => {
    setRemoving(user.username);
    setNotice(undefined);
    deleteUser(token, user.username)
      .then(() => {
        setNotice({ tone: "ok", text: `${user.email} no longer has access.` });
        reload();
      })
      .catch((err: unknown) => setNotice({ tone: "bad", text: message(err) }))
      .finally(() => {
        setRemoving(undefined);
        setConfirming(undefined);
      });
  };

  return (
    <>
      <form className="invite" onSubmit={invite}>
        <label className="log-control invite-field">
          <span className="label">Invite someone</span>
          <input
            type="email"
            value={email}
            placeholder="name@helloproper.com"
            autoComplete="off"
            required
            onChange={(event) => setEmail(event.target.value)}
          />
        </label>
        <button type="submit" className="button log-refresh" disabled={inviting || email.trim() === ""}>
          {inviting ? "Sending…" : "Send invitation"}
        </button>
      </form>
      {notice ? <p className={notice.tone === "bad" ? "error" : "admin-ok"}>{notice.text}</p> : null}

      {loadError ? (
        <p className="error">The accounts could not be listed: {loadError}</p>
      ) : users === undefined ? (
        <p className="admin-status">Loading…</p>
      ) : users.length === 0 ? (
        <p className="admin-status">No user pool here — in local development, nobody signs in.</p>
      ) : (
        <div className="table-scroll">
          <table className="users">
            <thead>
              <tr>
                <th>Email</th>
                <th>Status</th>
                <th>Invited</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {users.map((user) => {
                const isYou = user.email.toLowerCase() === viewer.toLowerCase();
                return (
                  <tr key={user.username} data-you={isYou}>
                    <td className="prose">
                      {user.email}
                      {isYou ? <span className="chip you">you</span> : null}
                    </td>
                    <td className="prose">
                      <span className="chip" data-tone={!user.enabled ? "bad" : user.status === "CONFIRMED" ? undefined : "quiet"}>
                        {!user.enabled ? "Disabled" : STATUS_LABEL[user.status]}
                      </span>
                    </td>
                    <td>{formatTimestamp(user.createdAt)}</td>
                    <td className="prose actions">
                      {isYou ? null : confirming === user.username ? (
                        <span className="confirm">
                          Remove {user.email}? Access ends at once.
                          <button type="button" className="linkish danger" disabled={removing === user.username} onClick={() => remove(user)}>
                            {removing === user.username ? "Removing…" : "Remove"}
                          </button>
                          <button type="button" className="linkish" disabled={removing === user.username} onClick={() => setConfirming(undefined)}>
                            Keep
                          </button>
                        </span>
                      ) : (
                        <button type="button" className="linkish" onClick={() => setConfirming(user.username)}>
                          Remove
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
